import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface DividerProps {
  /** Vertical orientation requires the parent to be a flex row. */
  orientation?: "horizontal" | "vertical";
  /** Shrink to content height/width when inside a flex container (MUI flexItem). */
  flexItem?: boolean;
  /** Indent to align with list item text (MUI inset variant). */
  inset?: boolean;
  /** Override the semantic border colour. */
  className?: string;
  children?: ReactNode;
}

/**
 * Horizontal or vertical hairline separator. Defaults to the semantic
 * `border-default` token (8% black light / 8% white dark).
 */
export function Divider({
  orientation = "horizontal",
  flexItem = false,
  inset = false,
  className,
  children,
}: DividerProps) {
  if (orientation === "vertical") {
    return (
      <hr
        role="separator"
        aria-orientation="vertical"
        className={cn(
          "w-px shrink-0 self-stretch border-0 bg-border-default",
          flexItem && "h-full",
          className,
        )}
      />
    );
  }

  if (children) {
    return (
      <div className={cn("flex items-center gap-3 py-1", className)} role="separator">
        <span className="h-px grow bg-border-default" />
        <span className="type-caption text-content-secondary">{children}</span>
        <span className="h-px grow bg-border-default" />
      </div>
    );
  }

  return (
    <hr
      role="separator"
      aria-orientation="horizontal"
      className={cn(
        "h-px w-full shrink-0 border-0 bg-border-default",
        inset && "ml-8",
        flexItem && "self-stretch",
        className,
      )}
    />
  );
}
